"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import Breadcrumb from "@/Components/Breadcrumb/Breadcrumb";

import "@splidejs/react-splide/css"; // Splide base styles
import styles from "./career-banner.module.scss";
import { toWebp } from "@/utils/common";

const Button = dynamic(() => import("@/Components/Button"));

interface CareerBannerProps {
  data?: any;
  isMobile?: boolean;
}

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL}`;

const getImageUrl = (url: string | undefined) => {
  if (!url) return "/images/fallback.png";
  if (url.startsWith("http")) return url;
  const normalizedBase = baseUrl.replace("/api", "").replace(/\/$/, "");
  const normalizedUrl = url.replace(/^\//, "");
  return `${normalizedBase}/${normalizedUrl}`;
};

const CareerBanner = ({ data }: CareerBannerProps) => {
  const [isScrollUp, setIsScrollUp] = useState(false);

  useEffect(() => {
    const element = document.getElementById("positions");
    if (element && isScrollUp) {
      window.scrollTo({
        top: element.offsetTop - 50,
        behavior: "instant",
      });
      setIsScrollUp(false);
    }
  }, [isScrollUp]);

  const bannerImages = data?.banner_images ?? [];

  const splideOptions = {
    type: "loop",
    drag: false,
    arrows: false,
    pagination: false,
    autoWidth: true,
    gap: "24px",
    clones: Math.max(bannerImages.length, 6), // extra clones for smoother loop
    autoScroll: {
      speed: 1, // adjust: smaller = slower, larger = faster
      pauseOnHover: true,
      pauseOnFocus: false,
    },
    breakpoints: {
      1440: { gap: "24px", slides: { perView: 5 } },
      1024: { gap: "24px", slides: { perView: 3 } },
      768: { gap: "16px", slides: { perView: 5 } },
      640: { gap: "16px" },
      320: { gap: "16px" },
    },
  };

  return (
    <section>
      <div className="request-proposal-banner bg-background-dark pt-[120px] lg:pt-[150px] pb-[56px] lg:pb-[80px] xl:pb-[100px] relative z-0">
        {/* Shapes */}
        <div className="banner-shape-1 absolute top-[0px] left-0 z-[1] w-[50%] md:w-[40%]">
          <Image
            src="/images/banner-left-layer-mobile.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full md:hidden"
          />
          <Image
            src="/images/banner-left-layer-tablet.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full hidden md:block lg:hidden"
          />
          <Image
            src="/images/banner-left-layer-desktop.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full hidden lg:block"
          />
        </div>
        <div className="banner-shape-1 absolute bottom-0 right-0 z-[-1] ">
          <Image
            src="/images/banner-right-layer-mobile.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full md:hidden"
          />
          <Image
            src="/images/banner-right-layer-tablet.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full hidden md:block lg:hidden"
          />
          <Image
            src="/images/banner-right-layer-desktop.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full hidden lg:block"
          />
        </div>

        {/* Content */}
        <div className="container">
          {data?.breadcrumbs?.item?.length > 0 && (
            <Breadcrumb
              items={data?.breadcrumbs?.item ?? []}
              className="mb-[16px] lg:mb-[32px] xl:mb-[40px] flex justify-center"
            />
          )}
          <div className="blog-heading mb-[32px]">
            <h1 className="text-center text-white font-extrabold tracking-[1px] uppercase text-display md:mb-[12px] mb-[24px]">
              {data?.title}
            </h1>
            <p className="max-w-[1000px] mx-auto text-secondary-text-white text-center font-regular text-subtitle-3">
              {data?.sub_title}
            </p>
          </div>

          <div className="flex items-center justify-center">
            <Button
              variant="small"
              className="my-0"
              label={data?.button_text}
              onClick={() => setIsScrollUp(true)}
            />
          </div>
        </div>

        {/* Splide marquee slider */}
        <div className="banner-slider bg-background-dark mt-[60px]">
          <Splide
            className={styles.careerSplide}
            options={splideOptions}
            extensions={{ AutoScroll }}
          >
            {bannerImages.map((item: any, index: number) => (
              <SplideSlide
                key={item.id ?? index}
                className={styles.careerSlide}
              >
                {item?.image?.data?.attributes?.url && (
                  <Image
                    src={toWebp(
                      getImageUrl(item?.image?.data?.attributes?.url),
                    )}
                    alt={`career banner ${index + 1}`}
                    width={285 * 2}
                    height={380 * 2}
                    quality={100}
                    className={styles.slideImage} // module class + keeps object-fit via css
                  />
                )}
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </div>
    </section>
  );
};

export default CareerBanner;
